import { useEffect, useState, useCallback } from "react";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { relTime } from "@/lib/format";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = items.filter((n) => !n.read).length;

  const load = useCallback(async () => {
    try {
      // First run alert check server-side (poll)
      await api.post("/alerts/check").catch(() => {});
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {
      // swallow — polling errors are non-fatal
      void e;
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-brand-deep transition-colors"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span
              data-testid="notification-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-loss text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0 border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <p className="font-display font-semibold text-sm">Notifications</p>
          <button
            onClick={markAll}
            data-testid="notification-mark-all-read"
            className="text-xs text-gray-500 hover:text-brand-deep flex items-center gap-1"
          >
            <Check size={12} /> Mark all read
          </button>
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-gray-500">
              No notifications yet.
            </div>
          ) : (
            items.map((n) => (
              <div
                key={n._id}
                data-testid={`notification-item-${n._id}`}
                className={`px-4 py-3 border-b border-gray-100 ${!n.read ? "bg-gray-50" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-black truncate">{n.title}</p>
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{relTime(n.created_at)}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
